import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, VariantSelectedEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    // Jump to the variant's photo the moment it is picked, without waiting for
    // the section fetch. See #handleVariantSelected.
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Shows the selected variant's photo immediately, before the server answers.
   *
   * variantUpdate below is the real update, but it only fires once the section
   * fetch comes back — measured on this store at ~300ms after the click. Until
   * then the gallery still shows the previous colour, which reads as the image
   * being slow to load.
   *
   * Every one of the product's media is already rendered as a slide, so nothing
   * needs fetching to show the right one: find the slide whose image is the
   * selected swatch's image and select it. variantUpdate then replaces the
   * gallery as it always did, landing on the same slide, so this only ever
   * removes the wait — it changes no end state.
   *
   * Matching is by filename, because the swatch and the slide reference the same
   * file at different widths. If anything does not line up we simply do nothing
   * and the original behaviour stands.
   *
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    try {
      const optionValueId = event?.detail?.resource?.id;
      if (!optionValueId) return;

      const input = document.querySelector(`input[data-option-value-id="${CSS.escape(optionValueId)}"]`);
      const label = input?.closest('label');
      if (!label) return;

      // The swatch's photo: an <img> on themes that render one, otherwise the
      // background-image the label paints.
      const swatchImg = label.querySelector('img');
      let url = swatchImg?.currentSrc || swatchImg?.getAttribute('src') || '';
      if (!url) {
        for (const el of [label, ...label.querySelectorAll('span')]) {
          const bg = getComputedStyle(el).backgroundImage;
          const match = bg && bg.match(/url\(["']?([^"')]+)/);
          if (match) { url = match[1]; break; }
        }
      }
      const file = url.split('/').pop()?.split('?')[0];
      if (!file) return;

      const slides = this.querySelectorAll('slideshow-slide');

      // If the photo is already a slide, just go to it. (True on themes that
      // render every media regardless of variant.)
      for (let i = 0; i < slides.length; i++) {
        const img = slides[i].querySelector('img');
        const src = img?.currentSrc || img?.getAttribute('src') || '';
        if (src.split('/').pop()?.split('?')[0] === file) {
          this.slideshow?.select(i, undefined, { animate: false });
          return;
        }
      }

      // It is not there. This gallery only holds the CURRENT colour's media —
      // checked on this store: picking Beige looks for a file that none of the
      // twelve slides carry, because the server filters them per variant. So
      // there is no slide to select, and the shopper waits for the fetch purely
      // to be shown an image whose URL the swatch was already telling us.
      //
      // Point the visible slide at it instead. The app preloads variant photos,
      // so this paints immediately; the theme's own update then replaces the
      // gallery as always and this frame is thrown away with it.
      const visible = this.#visibleSlide(slides);
      const target = visible?.querySelector('img');
      if (!target) return;

      // Rebuild the srcset for the new file at exactly the widths the old one
      // offered, so the browser picks the same size it would have picked
      // anyway — and so the copy the app preloaded is the copy it asks for.
      // Dropping srcset and setting a single src instead would make the browser
      // request a width nothing had warmed, and the preload would buy nothing.
      const base = url.split('?')[0];
      const query = url.split('?')[1] || '';
      const widths = (target.getAttribute('srcset') || '').match(/(\d+)w/g) || [];

      if (widths.length) {
        target.srcset = widths
          .map((w) => {
            const n = parseInt(w, 10);
            return `${base}?${query ? `${query}&` : ''}width=${n} ${n}w`;
          })
          .join(', ');
      } else {
        target.removeAttribute('srcset');
      }
      target.removeAttribute('data_max_resolution');
      target.src = `${base}?${query ? `${query}&` : ''}width=${Math.min(
        2048,
        Math.round((target.clientWidth || 800) * (window.devicePixelRatio || 1))
      )}`;
    } catch {
      // Purely an optimisation — never let it break the picker.
    }
  };

  /**
   * The slide currently centred in the gallery.
   *
   * @param {NodeListOf<Element>} slides - The gallery's slides.
   * @returns {Element | null}
   */
  #visibleSlide = (slides) => {
    const box = this.getBoundingClientRect();
    const centre = box.left + box.width / 2;
    let best = null;
    let bestDistance = Infinity;
    for (const slide of slides) {
      const rect = slide.getBoundingClientRect();
      if (rect.width < 40) continue;
      const distance = Math.abs(rect.left + rect.width / 2 - centre);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = slide;
      }
    }
    return best;
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
